import { DeprecatedIcon, OptionType, Pill, SortDirection, Typography } from '@fullstory/components';
import { ComponentProps, ReactNode } from 'react';
import { segmentsSelectors } from '@fullstory/redux';
import { DimensionNoConfig, LabelGroup } from './dimensionality';
import { intl } from '@fullstory/intl';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { SegmentDot } from '../components';
import {
  Dashboard,
  FunnelDetail,
  HeatmapDetail,
  HomeCard,
  MetricDetail,
  RetentionMetricDetail,
  SegmentDetail,
} from '@fullstory/proto/fs/services/dx-objects';

export enum CONVERTED_OPTIONS {
  CONVERTED = 'Completed',
  NOT_CONVERTED = 'Did not complete',
}

export const ConvertedOptionsDisplay = {
  [CONVERTED_OPTIONS.CONVERTED]: intl.formatMessage({ defaultMessage: 'Completed', id: '95stPq' }),
  [CONVERTED_OPTIONS.NOT_CONVERTED]: intl.formatMessage({
    defaultMessage: 'Did not complete',
    id: 'l57Nx2',
  }),
};

interface SelectPillColors {
  background: ComponentProps<typeof Pill>['color'];
  text: ComponentProps<typeof Typography>['color'];
}

export interface Theme {
  filterTextColor: ComponentProps<typeof Typography>['color'];
  objectTypeFilter: {
    default: SelectPillColors;
    selected: SelectPillColors;
  };
}

export interface SessionReplayDimension {
  dimensions: DimensionNoConfig[];
  dimensionValues: OptionType<LabelGroup>[];
  selectedValue: OptionType<LabelGroup> | null;
}

export enum DimensionContext {
  Default = 'default',
  DashboardsDimensionalityCard = 'dashboards dimensionality card',
  DashboardsTrendsCard = 'dashboards trends card',
  DashboardsFunnelCard = 'dashboards funnel card',
  Funnels = 'funnels pane',
  Metrics = 'metrics pane',
  HomeSearchDimensionalityCard = 'home search dimensionality card',
  HomeSearchTrendsCard = 'home search trends card',
  HomeSearchFunnelCard = 'home search funnels card',
  Retention = 'retention pane',
}

export interface SegmentsInfo {
  segmentsById: ReturnType<typeof segmentsSelectors.getSegmentsById>;
  segmentsLoading: ReturnType<typeof segmentsSelectors.getSegmentsLoading>;
}

export interface VisualizationLegendItemProps {
  label: string;
  color: ComponentProps<typeof SegmentDot>['color'];
  link?: string;
  previous?: boolean;
  tooltipVariant?: boolean;
  invertColors?: boolean;
  disableTooltip?: boolean;
}

export type Action = 'view' | 'duplicate' | 'archive' | 'delete' | 'star';

export type ItemType = {
  id: string;
  name: string;
  description: string;
  starred: boolean;
  archived: boolean;
  lastViewed: timestamp | null;
  lastUpdated: timestamp | null;
  created: timestamp | null;
  createdBy: string;
  lastUpdatedBy: string;
  actions?: Action[]; // The actions available to the given item. If not provided, the standard actions will be available.
  rightAdornment?: ReactNode;
  icon?: React.ComponentProps<typeof DeprecatedIcon>['name'];
};

export type ResourceListItemType = ItemType;

export enum LibraryCardType {
  metric = 'metric',
  funnel = 'funnel',
  segment = 'segment',
  dashboard = 'dashboard',
  retention = 'retention',
  heatmap = 'heatmap',
}

export interface LibraryCardItemType extends ItemType {
  type: LibraryCardType;
  metric?: MetricDetail;
  funnel?: FunnelDetail;
  retention?: RetentionMetricDetail;
  segment?: SegmentDetail;
  dashboard?: Dashboard;
  heatmap?: HeatmapDetail;
}

export type HomeCardWithoutId = Omit<HomeCard, 'id'>;

export interface LibrarySortState {
  sortColumnKey: LibraryTableSortOption;
  direction: SortDirection;
}

export const libraryTableSortOptions = ['name', 'lastModified', 'insightSort'] as const;
export type LibraryTableSortOption = (typeof libraryTableSortOptions)[number];

export { type RollingWindow } from '@fullstory/metrics-model';
